import { useCallback, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Send, AlertTriangle } from "lucide-react";
import GameTimer from "./GameTimer";
import AnswerButton from "./AnswerButton";
import { api, ApiError } from "@/lib/api";
import { useGameTimer } from "@/hooks/useGameTimer";
import type { StartGameSessionResult, SubmitAnswerResult, CompleteGameSessionResult } from "@/types/game";
import type { OptionLetter, StudentQuestion } from "@/types/database";

interface Props {
  studentId: string;
  session: StartGameSessionResult;
  onComplete: (result: CompleteGameSessionResult) => void;
}

interface AnsweredEntry {
  question_id: string;
  selected_option: OptionLetter | null;
  time_seconds: number;
  feedback?: SubmitAnswerResult;
}

export default function GamePlayer({ studentId, session, onComplete }: Props) {
  const questions: StudentQuestion[] = session.questions;
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<OptionLetter | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [answered, setAnswered] = useState<AnsweredEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);

  const current = questions[index];
  const progress = useMemo(() => `${index + 1} / ${questions.length}`, [index, questions.length]);
  const lastFeedback = answered[index]?.feedback;

  const onPerQuestionTimeout = useCallback(() => {
    if (submitting || answered[index]) return;
    void submit(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting, answered, index]);

  const onTotalTimeout = useCallback(() => {
    void completeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { questionRemainingSec, totalRemainingSec, questionElapsedSec } = useGameTimer({
    perQuestionLimitSec: session.time_limit_per_question_sec,
    totalLimitSec: session.total_time_limit_sec,
    onPerQuestionTimeout,
    onTotalTimeout,
    questionKey: current?.id,
  });

  async function submit(forceOption: OptionLetter | null) {
    if (submitting || answered[index]) return;
    if (!current) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.submitAnswer({
        game_session_id: (session as unknown as { session_id: string }).session_id,
        student_id: studentId,
        question_id: current.id,
        selected_option: forceOption,
        time_seconds: Math.min(questionElapsedSec, session.time_limit_per_question_sec),
      });
      const entry: AnsweredEntry = {
        question_id: current.id,
        selected_option: forceOption,
        time_seconds: questionElapsedSec,
        feedback: res,
      };
      const next = [...answered];
      next[index] = entry;
      setAnswered(next);
      setSelected(forceOption);
      // Advance after a short delay so any inline feedback can render.
      const showInline = session.show_explanations_inline && res.correct_option;
      const delay = showInline ? 1400 : 350;
      setTimeout(() => {
        if (index + 1 >= questions.length) {
          void completeSession();
        } else {
          setIndex(index + 1);
          setSelected(null);
        }
      }, delay);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // duplicate — treat as already answered, advance
        if (index + 1 >= questions.length) await completeSession();
        else { setIndex(index + 1); setSelected(null); }
      } else {
        setError(e instanceof Error ? e.message : "Could not save your answer.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function completeSession() {
    if (completing) return;
    setCompleting(true);
    try {
      const result = await api.completeGameSession((session as unknown as { session_id: string }).session_id);
      onComplete(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not finalize your game.");
    } finally {
      setCompleting(false);
    }
  }

  if (!current) {
    return (
      <div className="card-dark p-6 max-w-md mx-auto text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        <p className="mt-2">Loading questions…</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto w-full space-y-4"
    >
      <div className="flex items-center justify-between text-sm text-steel-300">
        <span>Question {progress}</span>
        <span>{Math.round(((index) / questions.length) * 100)}% complete</span>
      </div>

      <GameTimer
        questionRemainingSec={questionRemainingSec}
        questionLimitSec={session.time_limit_per_question_sec}
        totalRemainingSec={totalRemainingSec}
      />

      <div className="card-dark p-5">
        <AnimatePresence mode="wait">
          <motion.h2
            key={current.id}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            className="font-display text-xl text-white leading-snug"
          >
            {current.question_text}
          </motion.h2>
        </AnimatePresence>
        {current.category && (
          <div className="mt-2"><span className="pill-forge">{current.category}</span></div>
        )}
      </div>

      <div className="grid gap-3">
        {(["A", "B", "C", "D"] as const).map((L) => {
          const text =
            L === "A" ? current.option_a :
            L === "B" ? current.option_b :
            L === "C" ? current.option_c : current.option_d;
          const reveal: "correct" | "incorrect" | null =
            lastFeedback && session.show_explanations_inline
              ? lastFeedback.correct_option === L
                ? "correct"
                : selected === L && lastFeedback.is_correct === false
                ? "incorrect"
                : null
              : null;
          return (
            <AnswerButton
              key={L}
              letter={L}
              text={text}
              selected={selected === L}
              disabled={submitting || !!answered[index]}
              reveal={reveal}
              onSelect={() => setSelected(L)}
            />
          );
        })}
      </div>

      {error && (
        <div className="pill-bad px-3 py-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <button
        className="btn-primary w-full text-lg"
        onClick={() => submit(selected)}
        disabled={submitting || !selected || !!answered[index] || completing}
      >
        {submitting || completing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {index + 1 === questions.length ? "Submit & Finish" : "Submit"}
      </button>
    </motion.div>
  );
}
